import React, { useState } from "react";
import { useGetMachinesByWilayaQuery } from "@/features/models/statisticsApi";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import LoadingDots from "@/components/shared/LoadingDots";
import { Map } from "algeria-map-ts";
import { Monitor, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";

const Dashboard = () => {
  const [selectedWilaya, setSelectedWilaya] = useState(null);
  const [lastSeenFilter, setLastSeenFilter] = useState(false);
  const { data, isLoading, error } = useGetMachinesByWilayaQuery(
    lastSeenFilter,
    {
      refetchOnMountOrArgChange: true,
    }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingDots />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-red-500">Failed to load dashboard data</div>
      </div>
    );
  }

  const wilayaData = data?.data || [];
  const summary = data?.summary || {};

  // Prepare map data
  const mapData = {};
  const maxMachines = Math.max(...wilayaData.map((item) => item.total), 1);

  wilayaData.forEach((item) => {
    const intensity = item.total / maxMachines;
    const lightness = 85 - intensity * 60;
    mapData[item.wilaya] = {
      value: item.total,
      color: `hsl(142, 70%, ${lightness}%)`,
      wilaya: item.wilaya,
      size86: item.size86,
      size65: item.size65,
    };
  });

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Machines Distribution</h1>
          <p className="text-muted-foreground mt-1">
            Geographic distribution of machines across Algeria
          </p>
        </div>{" "}
        <div className="flex gap-4 items-center">
          <Button
            className="px-2 py-1"
            size="small"
            variant={lastSeenFilter === false ? "default" : "outline"}
            onClick={() => setLastSeenFilter(false)}
          >
            All time
          </Button>
          <Button
            className="px-2 py-1"
            size="small"
            variant={lastSeenFilter === true ? "default" : "outline"}
            onClick={() => setLastSeenFilter(true)}
          >
            Past Month
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="p-3">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium">
              Total Machines
            </CardTitle>
            <Monitor className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary.totalMachines || 0}
            </div>
          </CardContent>
        </Card>

        <Card className="p-3">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium">86" Screens</CardTitle>
            <Monitor className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.total86 || 0}</div>
            <p className="text-xs text-muted-foreground">
              {summary.totalMachines
                ? Math.round((summary.total86 / summary.totalMachines) * 100)
                : 0}
              % of total
            </p>
          </CardContent>
        </Card>

        <Card className="p-3">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium">65" Screens</CardTitle>
            <Monitor className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.total65 || 0}</div>
            <p className="text-xs text-muted-foreground">
              {summary.totalMachines
                ? Math.round((summary.total65 / summary.totalMachines) * 100)
                : 0}
              % of total
            </p>
          </CardContent>
        </Card>

        <Card className="p-3">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium">Wilayas</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary.totalWilayas || 0}
            </div>
            <p className="text-xs text-muted-foreground">Active locations</p>
          </CardContent>
        </Card>
      </div>

      {/* Map */}
      <Card>
        <CardHeader>
          <CardTitle>Map View</CardTitle>
          <CardDescription>
            Hover over any wilaya to see machine count. Click to view details.
            Darker green indicates more machines.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="w-full flex justify-center items-center">
            <Map
              color="hsl(142, 50%, 90%)"
              HoverColor="hsl(142, 60%, 50%)"
              stroke="#e5e7eb"
              hoverStroke="hsl(142, 70%, 35%)"
              height="700px"
              width="100%"
              data={mapData}
              onWilayaClick={(wilayaName, machineCount) => {
                const wilayaInfo = mapData[wilayaName];
                setSelectedWilaya({
                  name: wilayaName,
                  count: machineCount,
                  size86: wilayaInfo?.size86 || 0,
                  size65: wilayaInfo?.size65 || 0,
                });
              }}
              getHoverContent={(wilayaData) => {
                return `
                  <div style="padding: 12px !important; min-width: 180px !important; white-space: nowrap;">
                    <div style="font-weight: 700; font-size: 16px; color: #1a1a1a; margin-bottom: 6px;">
                      ${wilayaData.wilaya || "No data"}
                    </div>
                    <div style="font-size: 14px; color: #666; margin-bottom: 4px;">
                      <span style="font-weight: 600;">Total:</span> ${
                        wilayaData.value || 0
                      }
                    </div>
                    <div style="font-size: 12px; color: #999;">
                      86": ${wilayaData.size86 || 0} | 65": ${
                  wilayaData.size65 || 0
                }
                    </div>
                  </div>
                `;
              }}
              hoverContentStyle={{
                backgroundColor: "white",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                boxShadow:
                  "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                fontSize: "14px",
                pointerEvents: "none",
                zIndex: 1000,
                minWidth: "180px",
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog
        open={!!selectedWilaya}
        onOpenChange={(open) => !open && setSelectedWilaya(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedWilaya?.name}</DialogTitle>
            <DialogDescription>Machine distribution details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="flex flex-col p-4 bg-muted rounded-lg">
                <span className="text-sm text-muted-foreground mb-1">
                  Total
                </span>
                <span className="text-2xl font-bold">
                  {selectedWilaya?.count || 0}
                </span>
              </div>
              <div className="flex flex-col p-4 bg-muted rounded-lg">
                <span className="text-sm text-muted-foreground mb-1">
                  86" Screens
                </span>
                <span className="text-2xl font-bold">
                  {selectedWilaya?.size86 || 0}
                </span>
              </div>
              <div className="flex flex-col p-4 bg-muted rounded-lg">
                <span className="text-sm text-muted-foreground mb-1">
                  65" Screens
                </span>
                <span className="text-2xl font-bold">
                  {selectedWilaya?.size65 || 0}
                </span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
